import type { GraphQLFormattedError } from 'graphql';

import type { AuthErrorCode } from './sessionClient';

/** Fallback copy for auth submissions that fail without a user-facing message. */
export const GENERIC_AUTH_FAILURE_MESSAGE = 'Something went wrong. Please try again.';

// Expected credential outcomes of a login attempt — the user mistyped or the
// account is (about to be) locked. These are surfaced in-form and are never
// worth an error reporter's attention, unlike network/5xx/unknown failures.
const EXPECTED_LOGIN_FAILURE_CODES: ReadonlySet<AuthErrorCode> = new Set([
  'invalid-credentials',
  'lockout-imminent',
  'locked',
]);

export const isExpectedLoginFailure = (code: AuthErrorCode): boolean =>
  EXPECTED_LOGIN_FAILURE_CODES.has(code);

type GraphQLAuthErrorCode = 'UNAUTHENTICATED' | 'FORBIDDEN' | 'REAUTHENTICATION_REQUIRED';

type GraphQLAuthError = Pick<GraphQLFormattedError, 'message' | 'extensions'> & {
  code?: string;
};

const IMPERSONATION_DENIED_MESSAGE = 'This action is not available while impersonating a customer.';
const PERMISSION_DENIED_MESSAGE = 'You do not have permission to perform this action.';

export function getGraphQLAuthErrorCode(error: GraphQLAuthError): GraphQLAuthErrorCode | undefined {
  const code: unknown = error.extensions?.['code'] ?? error.code;
  switch (code) {
    case 'UNAUTHENTICATED':
    case 'FORBIDDEN':
    case 'REAUTHENTICATION_REQUIRED':
      return code;
  }
  return undefined;
}

export function getGraphQLAuthErrorMessage(error: GraphQLAuthError): string | undefined {
  switch (getGraphQLAuthErrorCode(error)) {
    case 'UNAUTHENTICATED':
      return 'Please sign in to continue.';
    case 'REAUTHENTICATION_REQUIRED':
      return 'Please sign in again to continue.';
    case 'FORBIDDEN':
      return error.message === IMPERSONATION_DENIED_MESSAGE
        ? IMPERSONATION_DENIED_MESSAGE
        : PERMISSION_DENIED_MESSAGE;
  }
  return undefined;
}

export const isGraphQLErrorUnauthorized = (error: GraphQLAuthError): boolean => {
  const code = getGraphQLAuthErrorCode(error);
  return code === 'FORBIDDEN' || code === 'REAUTHENTICATION_REQUIRED';
};

export const isGraphQLErrorReauthRequired = (error: GraphQLAuthError): boolean =>
  getGraphQLAuthErrorCode(error) === 'REAUTHENTICATION_REQUIRED';
